// AI Config Console (M8.9 + M8.10 + M26 routing split).
//
// Agent picker (4 agents) × 5 tabs (Behavior, Boundaries, Persona,
// Knowledge, Sandbox). Each tab has its own canonical URL — the
// `tab` prop is supplied by the router (/ai/behavior, /ai/boundaries,
// /ai/persona, /ai/knowledge, /ai/sandbox) so each tab is deep-linkable.
//
// Behavior / Boundaries / Persona are JSON editors over AIConfigPatch.
// Knowledge uploads PDFs into the vault-protected blob store.
// Sandbox is a placeholder until the agent-replay surface ships.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Beaker, Save, Trash2, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import type {
  AIConfig,
  AIConfigPatch,
  KnowledgeDoc,
} from "../lib/api";
import { uploadKnowledgeViaFetch } from "../lib/uploadKnowledge";

const AGENTS = [
  { key: "khidmat", labelKey: "policies.ai_config_agent_khidmat" },
  { key: "tashghil", labelKey: "policies.ai_config_agent_tashghil" },
  { key: "rawatib", labelKey: "policies.ai_config_agent_rawatib" },
  { key: "mutabaa", labelKey: "policies.ai_config_agent_mutabaa" },
];

const TABS = [
  { key: "behavior", labelKey: "policies.ai_config_tab_behavior", to: "/ai/behavior" },
  { key: "boundaries", labelKey: "policies.ai_config_tab_boundaries", to: "/ai/boundaries" },
  { key: "persona", labelKey: "policies.ai_config_tab_persona", to: "/ai/persona" },
  { key: "knowledge", labelKey: "policies.ai_config_tab_knowledge", to: "/ai/knowledge" },
  { key: "sandbox", labelKey: "policies.ai_config_tab_sandbox", to: "/ai/sandbox" },
] as const;

export type AIConfigTab = (typeof TABS)[number]["key"];

const _TAB_HEADER_KEYS: Record<
  AIConfigTab,
  { titleKey: string; descKey: string }
> = {
  behavior: {
    titleKey: "policies.ai_config_tab_behavior",
    descKey: "policies.ai_config_behavior_desc",
  },
  boundaries: {
    titleKey: "policies.ai_config_tab_boundaries",
    descKey: "policies.ai_config_boundaries_desc",
  },
  persona: {
    titleKey: "policies.ai_config_tab_persona",
    descKey: "policies.ai_config_persona_desc",
  },
  knowledge: {
    titleKey: "policies.ai_config_tab_knowledge",
    descKey: "policies.ai_config_knowledge_desc",
  },
  sandbox: {
    titleKey: "policies.ai_config_tab_sandbox",
    descKey: "policies.ai_config_sandbox_desc",
  },
};

export function AIConfigPage({ tab = "behavior" }: { tab?: AIConfigTab } = {}) {
  const { t } = useTranslation();
  const { data: entities, isLoading } = useQuery({
    queryKey: ["legal-entities", false, ""],
    queryFn: () => api.listLegalEntities({}),
  });
  const legalEntityId = entities?.[0]?.id ?? null;

  const [agent, setAgent] = useState<string>(AGENTS[0].key);
  const header = _TAB_HEADER_KEYS[tab];

  if (isLoading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600">
        {t("common.loading")}
      </div>
    );
  }

  if (!legalEntityId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        {t("policies.ai_config_setup_first")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-text-primary">
          {t(header.titleKey)}
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          {t(header.descKey)}
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {AGENTS.map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => setAgent(a.key)}
            className={[
              "rounded-md px-3 py-1.5 text-sm font-medium",
              agent === a.key
                ? "bg-brand text-brand-fg"
                : "border border-border-subtle text-text-secondary hover:bg-surface-sunken",
            ].join(" ")}
          >
            {t(a.labelKey)}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-border-subtle bg-surface-default">
        <div
          role="tablist"
          aria-label={t("policies.ai_config_tabs_aria")}
          className="flex border-b border-border-subtle"
        >
          {TABS.map((tabItem) => {
            const active = tab === tabItem.key;
            return (
              <Link
                key={tabItem.key}
                to={tabItem.to}
                role="tab"
                aria-selected={active}
                aria-current={active ? "page" : undefined}
                className={[
                  "px-4 py-2.5 text-sm font-medium border-b-2",
                  active
                    ? "border-brand text-text-primary"
                    : "border-transparent text-text-tertiary hover:text-text-primary",
                ].join(" ")}
              >
                {t(tabItem.labelKey)}
              </Link>
            );
          })}
        </div>
        <div className="p-5">
          {tab === "knowledge" ? (
            <KnowledgeTab legalEntityId={legalEntityId} />
          ) : tab === "sandbox" ? (
            <SandboxTab />
          ) : (
            <AgentConfigTab
              agent={agent}
              legalEntityId={legalEntityId}
              tab={tab}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Behavior / Boundaries / Persona tabs -----------------------

const TAB_FIELD: Record<
  Exclude<AIConfigTab, "knowledge" | "sandbox" | "behavior">,
  keyof AIConfigPatch
> = {
  boundaries: "boundaries",
  persona: "persona",
};

const KSA_BUSINESS_HOURS = {
  timezone: "Asia/Riyadh",
  days_active: ["sun", "mon", "tue", "wed", "thu"],
  start: "08:00",
  end: "17:00",
  after_hours_behavior: "escalate",
};

function AgentConfigTab({
  agent,
  legalEntityId,
  tab,
}: {
  agent: string;
  legalEntityId: string;
  tab: Exclude<AIConfigTab, "knowledge" | "sandbox">;
}) {
  if (tab === "behavior") {
    return (
      <BehaviorTab agent={agent} legalEntityId={legalEntityId} />
    );
  }
  return (
    <SingleJsonField
      agent={agent}
      legalEntityId={legalEntityId}
      field={TAB_FIELD[tab]}
    />
  );
}

function BehaviorTab({
  agent,
  legalEntityId,
}: {
  agent: string;
  legalEntityId: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["ai-configs", legalEntityId],
    queryFn: () => api.listAIConfigs(legalEntityId),
  });

  const config: AIConfig | undefined = data?.configs.find(
    (c) => c.agent_name === agent,
  );

  const [thresholdsDraft, setThresholdsDraft] = useState<string>("");
  const [hoursDraft, setHoursDraft] = useState<string>("");
  useEffect(() => {
    if (config) {
      setThresholdsDraft(
        JSON.stringify(config.auto_resolve_thresholds, null, 2),
      );
      setHoursDraft(JSON.stringify(config.working_hours, null, 2));
    }
  }, [config]);

  const mut = useMutation({
    mutationFn: () => {
      const thresholds = JSON.parse(thresholdsDraft);
      const hours = JSON.parse(hoursDraft);
      const patch: AIConfigPatch = {
        auto_resolve_thresholds: thresholds,
        working_hours: hours,
      };
      return api.updateAIConfig(agent, legalEntityId, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-configs", legalEntityId] });
    },
  });

  if (isLoading || !config) {
    return (
      <div className="text-text-tertiary">{t("policies.ai_config_loading")}</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Auto-resolve thresholds section */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-text-primary">
          {t("policies.ai_config_section_thresholds")}
        </h2>
        <p className="text-xs text-text-tertiary">
          {t("policies.ai_config_section_thresholds_hint")}
        </p>
        <textarea
          value={thresholdsDraft}
          onChange={(e) => setThresholdsDraft(e.target.value)}
          rows={8}
          className="w-full rounded-md border border-border-subtle bg-surface-sunken p-3 font-mono text-xs text-text-primary focus:border-brand focus:outline-none"
          spellCheck={false}
        />
      </section>

      {/* Working hours section */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-text-primary">
          {t("policies.ai_config_section_hours")}
        </h2>
        <p className="text-xs text-text-tertiary">
          {t("policies.ai_config_section_hours_hint")}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setHoursDraft(JSON.stringify({}, null, 2))}
            className="rounded-md border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:bg-surface-sunken"
          >
            {t("policies.ai_config_hours_24_7_btn")}
          </button>
          <button
            type="button"
            onClick={() =>
              setHoursDraft(JSON.stringify(KSA_BUSINESS_HOURS, null, 2))
            }
            className="rounded-md border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:bg-surface-sunken"
          >
            {t("policies.ai_config_hours_business_btn")}
          </button>
        </div>
        <textarea
          value={hoursDraft}
          onChange={(e) => setHoursDraft(e.target.value)}
          rows={8}
          className="w-full rounded-md border border-border-subtle bg-surface-sunken p-3 font-mono text-xs text-text-primary focus:border-brand focus:outline-none"
          spellCheck={false}
        />
      </section>

      {mut.isError && (
        <p className="text-sm text-rose-700">
          {(mut.error as Error).message}
        </p>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => {
            try {
              JSON.parse(thresholdsDraft);
              JSON.parse(hoursDraft);
              mut.mutate();
            } catch (e) {
              alert(
                t("policies.ai_config_invalid_json", {
                  msg: (e as Error).message,
                }),
              );
            }
          }}
          disabled={mut.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" />
          {mut.isPending
            ? t("policies.ai_config_saving")
            : t("policies.ai_config_save")}
        </button>
      </div>
    </div>
  );
}

function SingleJsonField({
  agent,
  legalEntityId,
  field,
}: {
  agent: string;
  legalEntityId: string;
  field: keyof AIConfigPatch;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["ai-configs", legalEntityId],
    queryFn: () => api.listAIConfigs(legalEntityId),
  });

  const config: AIConfig | undefined = data?.configs.find(
    (c) => c.agent_name === agent,
  );

  const [draft, setDraft] = useState<string>("");
  useEffect(() => {
    if (config) {
      setDraft(JSON.stringify(config[field as keyof AIConfig], null, 2));
    }
  }, [config, field]);

  const mut = useMutation({
    mutationFn: () => {
      const parsed = JSON.parse(draft);
      const patch: AIConfigPatch = { [field]: parsed };
      return api.updateAIConfig(agent, legalEntityId, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-configs", legalEntityId] });
    },
  });

  if (isLoading || !config) {
    return <div className="text-text-tertiary">{t("policies.ai_config_loading")}</div>;
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-text-tertiary">
        {t("policies.ai_config_json_hint")}
      </p>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={14}
        className="w-full rounded-md border border-border-subtle bg-surface-sunken p-3 font-mono text-xs text-text-primary focus:border-brand focus:outline-none"
        spellCheck={false}
      />
      {mut.isError && (
        <p className="text-sm text-rose-700">
          {(mut.error as Error).message}
        </p>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => {
            try {
              JSON.parse(draft);
              mut.mutate();
            } catch (e) {
              alert(t("policies.ai_config_invalid_json", { msg: (e as Error).message }));
            }
          }}
          disabled={mut.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" />
          {mut.isPending
            ? t("policies.ai_config_saving")
            : t("policies.ai_config_save")}
        </button>
      </div>
    </div>
  );
}

// ---------- Knowledge tab ---------------------------------------------

function KnowledgeTab({ legalEntityId }: { legalEntityId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["knowledge", legalEntityId],
    queryFn: () => api.listKnowledge(legalEntityId),
  });

  const patchMut = useMutation({
    mutationFn: ({
      docId,
      scope,
    }: {
      docId: string;
      scope: string;
    }) => api.patchKnowledgeScope(docId, legalEntityId, scope),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["knowledge", legalEntityId] }),
  });

  const deleteMut = useMutation({
    mutationFn: (docId: string) =>
      api.deleteKnowledge(docId, legalEntityId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["knowledge", legalEntityId] }),
  });

  return (
    <div className="space-y-4">
      <KnowledgeUploader legalEntityId={legalEntityId} />
      {isLoading || !data ? (
        <p className="text-text-tertiary">{t("policies.ai_config_loading")}</p>
      ) : data.documents.length === 0 ? (
        <p className="rounded border border-border-subtle bg-surface-sunken p-4 text-sm text-text-secondary">
          {t("policies.ai_config_no_docs")}
        </p>
      ) : (
        <ul className="divide-y divide-border-subtle rounded border border-border-subtle">
          {data.documents.map((d: KnowledgeDoc) => (
            <li key={d.id} className="flex items-center gap-3 p-3 text-sm">
              <span className="flex-1">
                <span className="font-medium text-text-primary">{d.filename}</span>
                <span className="ms-2 text-xs text-text-tertiary">
                  {t("policies.ai_config_doc_size", {
                    kb: Math.round(d.size_bytes / 1024),
                    date: new Date(d.uploaded_at).toLocaleDateString(),
                  })}
                </span>
              </span>
              <select
                value={d.scope}
                onChange={(e) =>
                  patchMut.mutate({ docId: d.id, scope: e.target.value })
                }
                className="rounded-md border border-border-subtle px-2 py-1 text-xs"
              >
                <option value="read_and_quote">{t("policies.ai_config_scope_read_and_quote")}</option>
                <option value="read_no_quote">{t("policies.ai_config_scope_read_no_quote")}</option>
                <option value="never_read">{t("policies.ai_config_scope_never_read")}</option>
              </select>
              <button
                type="button"
                onClick={() => {
                  if (confirm(t("policies.ai_config_delete_doc_confirm", { filename: d.filename }))) {
                    deleteMut.mutate(d.id);
                  }
                }}
                className="rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KnowledgeUploader({ legalEntityId }: { legalEntityId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [scope, setScope] = useState("read_and_quote");
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function upload(file: File) {
    setUploading(true);
    setErr(null);
    try {
      await uploadKnowledgeViaFetch(legalEntityId, file, scope);
      qc.invalidateQueries({ queryKey: ["knowledge", legalEntityId] });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="rounded border border-dashed border-border-subtle p-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="cursor-pointer">
          <span className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-sunken">
            <Upload className="h-3.5 w-3.5" />
            {uploading
              ? t("policies.ai_config_uploading")
              : t("policies.ai_config_choose_pdf")}
          </span>
          <input
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload(f);
            }}
          />
        </label>
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          className="rounded-md border border-border-subtle px-2 py-1.5 text-sm"
        >
          <option value="read_and_quote">{t("policies.ai_config_scope_read_and_quote")}</option>
          <option value="read_no_quote">{t("policies.ai_config_scope_read_no_quote")}</option>
          <option value="never_read">{t("policies.ai_config_scope_never_read")}</option>
        </select>
      </div>
      {err && <p className="mt-2 text-sm text-rose-700">{err}</p>}
    </div>
  );
}

// ---------- Sandbox tab — placeholder ---------------------------------

function SandboxTab() {
  const { t } = useTranslation();
  return (
    <div className="rounded border border-dashed border-border-subtle bg-surface-sunken p-6 text-center">
      <Beaker
        className="mx-auto h-8 w-8 text-text-tertiary"
        aria-hidden="true"
      />
      <h2 className="mt-3 text-base font-semibold text-text-primary">
        {t("policies.ai_config_sandbox_soon")}
      </h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-text-secondary">
        {t("policies.ai_config_sandbox_body")}
      </p>
      <p className="mt-3 text-xs text-text-tertiary">
        {t("policies.ai_config_sandbox_hint")}
      </p>
    </div>
  );
}
